import store from '../../state';
import authService from '../../services/AuthService';
import LayoutManager from '../../services/LayoutManager';
import {
  CANNED_ACTIVITIES_CONFIG,
  MASTER_LAYER_CATALOG,
  LAYER_CATEGORIES,
  getAllActivities
} from '../../config';
import { CustomActivityConfig } from '../../types';
import themeService, { THEMES_LIST, ThemeId } from '../../services/ThemeService';

export class AccountPanel {
  private container: HTMLElement | null = null;
  private activeTab: 'account' | 'locations' | 'feedback' | 'activities' | 'performance' = 'account';
  private keydownHandler: ((e: KeyboardEvent) => void) | null = null;

  // Custom Activity Creator / Editor State
  private isCreatingCustomActivity = false;
  private editingCustomActivityId: string | null = null;
  private customTitle = '';
  private customIcon = '🏔️';
  private customBasemap = 'road';
  private tempPrimaryLayers: Set<string> = new Set();
  private tempSecondaryLayers: Set<string> = new Set();
  private layerSearchQuery = '';
  private collapsedCategories: Set<string> = new Set();

  // Drag & drop state for activities reordering
  private draggedActivityId: string | null = null;

  // Feedback / Bug Report Form State
  private feedbackCategory: 'bug' | 'feature' | 'feedback' = 'bug';
  private feedbackSubject = '';
  private feedbackMessage = '';
  private feedbackIncludeDiagnostics = true;
  private feedbackSubmitting = false;
  private feedbackStatus: { type: 'success' | 'error'; message: string } | null = null;

  constructor() {
    (window as any).__accountPanel = this;

    store.subscribe((state) => {
      if (state.isProfileModalOpen && !this.container) {
        this.open();
      } else if (!state.isProfileModalOpen && this.container) {
        this.close();
      }
    });
  }

  public open(defaultTab: 'account' | 'locations' | 'feedback' | 'activities' | 'performance' = 'account'): void {
    this.activeTab = defaultTab;
    this.isCreatingCustomActivity = false;
    this.editingCustomActivityId = null;

    if (this.container) {
      if (this.keydownHandler) {
        window.removeEventListener('keydown', this.keydownHandler);
        this.keydownHandler = null;
      }
      if (this.container.parentNode) {
        this.container.parentNode.removeChild(this.container);
      }
      this.container = null;
    }

    this.container = document.createElement('div');
    this.container.id = 'gw-profile-modal-overlay';
    this.container.className = 'gw-modal-backdrop';
    document.body.appendChild(this.container);

    if (!store.getState().isProfileModalOpen) {
      store.update({ isProfileModalOpen: true });
    }

    this.render();

    this.keydownHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        this.close();
      }
    };
    window.addEventListener('keydown', this.keydownHandler);
  }

  public close(): void {
    if (this.keydownHandler) {
      window.removeEventListener('keydown', this.keydownHandler);
      this.keydownHandler = null;
    }

    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }

    if (store.getState().isProfileModalOpen) {
      store.update({ isProfileModalOpen: false });
    }
  }

  private async syncActivitiesToServer(): Promise<void> {
    const user = (authService as any).auth?.currentUser;
    if (!user) return;
    try {
      const idToken = await user.getIdToken(false);
      const apiBase = authService.getApiBase();
      const state = store.getState();
      await fetch(`${apiBase}/api/user/activities`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          enabledActivities: state.enabledActivities,
          customActivities: state.customActivities,
          activeActivityId: state.activePersona
        })
      });
    } catch (e) {
      console.warn('[AccountPanel] Could not sync activities with server:', e);
    }
  }

  private render(): void {
    if (!this.container) return;

    const user = store.getState().user;
    const email = user?.email || 'user@example.com';
    const name = user?.displayName || email.split('@')[0] || 'SkySkout Explorer';
    const initials = name.substring(0, 2).toUpperCase();
    const sub = store.getState().subscription;
    const status = sub?.status || 'none';
    const plan = sub?.plan;

    let headerBadgeText = 'SkySkout Explorer (Free)';
    let headerBadgeClass = 'gw-sub-badge-free';

    if (status === 'active') {
      headerBadgeClass = 'gw-sub-badge-active';
      if (plan === '12_month') headerBadgeText = 'SkySkout Pro (Annual)';
      else if (plan === '6_month') headerBadgeText = 'SkySkout Pro (6-Month)';
      else if (plan === '1_month') headerBadgeText = 'SkySkout Pro (Monthly)';
      else headerBadgeText = 'SkySkout Pro';
    } else if (status === 'trialing') {
      headerBadgeClass = 'gw-sub-badge-trial';
      headerBadgeText = 'SkySkout Pro (Free Trial)';
    } else if (status === 'comped') {
      headerBadgeClass = 'gw-sub-badge-active';
      headerBadgeText = 'SkySkout Pro (VIP)';
    }

    this.container.innerHTML = `
      <div class="gw-profile-dialog" role="dialog" aria-modal="true" aria-labelledby="gw-profile-title">
        <div class="gw-profile-header">
          <div class="gw-profile-user-summary">
            <div class="gw-profile-avatar">
              ${user?.photoURL ? `
                <img src="${user.photoURL}" alt="${name}" class="gw-profile-avatar-img" />
              ` : `
                <span class="gw-profile-avatar-text">${initials}</span>
              `}
            </div>
            <div class="gw-profile-user-details">
              <h2 id="gw-profile-title" class="gw-profile-name">${this.escapeHtml(name)}</h2>
              <span class="gw-profile-email">${this.escapeHtml(email)}</span>
              <span class="gw-profile-tier-badge ${headerBadgeClass}">${headerBadgeText}</span>
            </div>
          </div>
          <button id="gw-profile-close-btn" class="gw-auth-close" title="Close" aria-label="Close">&times;</button>
        </div>

        <!-- Navigation Tabs -->
        <div class="gw-profile-tabs-nav">
          <button id="gw-tab-btn-account" class="gw-profile-tab-nav-btn ${this.activeTab === 'account' ? 'active' : ''}">
            👤 Profile
          </button>
          <button id="gw-tab-btn-activities" class="gw-profile-tab-nav-btn ${this.activeTab === 'activities' ? 'active' : ''}">
            🎯 My Activities
          </button>
          <button id="gw-tab-btn-locations" class="gw-profile-tab-nav-btn ${this.activeTab === 'locations' ? 'active' : ''}">
            📍 Saved Locations
          </button>
          <button id="gw-tab-btn-feedback" class="gw-profile-tab-nav-btn ${this.activeTab === 'feedback' ? 'active' : ''}">
            💬 Feedback / Bug Report
          </button>
          <button id="gw-tab-btn-performance" class="gw-profile-tab-nav-btn ${this.activeTab === 'performance' ? 'active' : ''}">
            ⚡ Performance
          </button>
        </div>

        <!-- Tab Content Body -->
        <div class="gw-profile-body">
          ${this.renderTabContent()}
        </div>

        <!-- Footer Actions -->
        <div class="gw-profile-footer">
          <button id="gw-profile-signout-btn" class="gw-profile-btn-signout">
            <span>Sign Out</span>
          </button>
          <button id="gw-profile-done-btn" class="gw-auth-btn-primary" style="width: auto; padding: 8px 20px;">
            <span>Done</span>
          </button>
        </div>
      </div>
    `;

    this.bindEvents();
  }

  private renderTabContent(): string {
    if (this.activeTab === 'performance') {
      return this.renderPerformanceTab();
    }

    if (this.activeTab === 'activities') {
      return this.renderActivitiesTab();
    }

    if (this.activeTab === 'locations') {
      return `
        <div class="gw-profile-tab-section">
          <div class="gw-profile-section-header">
            <div>
              <h3 class="gw-profile-section-title">Saved Locations</h3>
              <p class="gw-profile-section-desc">Pin mountain peaks, vantage points, and dark sky sites for instant conditions.</p>
            </div>
            <button id="gw-profile-add-location-btn" class="gw-profile-btn-action">
              <span>+ Save Current Center</span>
            </button>
          </div>

          <div class="gw-profile-empty-card">
            <span class="gw-profile-empty-icon">🗺️</span>
            <h4>No Saved Locations Yet</h4>
            <p>Use the search bar or move the map to your favorite vantage point, then click "+ Save Current Center" to bookmark it.</p>
          </div>

          <div class="gw-profile-sample-box">
            <span class="gw-profile-sample-tag">Upcoming Feature</span>
            <p>Saved locations will automatically synchronize across your devices with custom altitude offsets and line-of-sight alpenglow azimuth angles.</p>
          </div>
        </div>
      `;
    }

    if (this.activeTab === 'feedback') {
      const subjectPlaceholder =
        this.feedbackCategory === 'bug'
          ? 'e.g., Layer failed to load when zooming into Mt. Rainier'
          : this.feedbackCategory === 'feature'
            ? 'e.g., Add marine swell period overlay for coastal kayaking'
            : 'e.g., Love the 10-day detailed forecast layout!';
      const messagePlaceholder =
        this.feedbackCategory === 'bug'
          ? 'Describe what happened, what you expected to see, and any steps to reproduce the issue...'
          : this.feedbackCategory === 'feature'
            ? 'Describe the feature or data layer you would like to see and how it would help your planning...'
            : 'Share your thoughts, impressions, or suggestions with the SkySkout team...';

      return `
        <div class="gw-profile-tab-section">
          <div class="gw-profile-section-header">
            <div>
              <h3 class="gw-profile-section-title">Feedback & Bug Reports</h3>
              <p class="gw-profile-section-desc">Send bug reports, feature requests, or general feedback directly to the SkySkout engineering team.</p>
            </div>
          </div>

          ${this.feedbackStatus ? `
            <div class="gw-feedback-banner ${this.feedbackStatus.type === 'success' ? 'gw-feedback-banner-success' : 'gw-feedback-banner-error'}" role="status">
              <span>${this.feedbackStatus.type === 'success' ? '✅' : '⚠️'}</span>
              <span>${this.escapeHtml(this.feedbackStatus.message)}</span>
            </div>
          ` : ''}

          <div class="gw-feedback-form">
            <div class="gw-feedback-field">
              <label class="gw-feedback-label">Report Type</label>
              <div class="gw-feedback-type-grid">
                <button type="button" class="gw-feedback-type-btn ${this.feedbackCategory === 'bug' ? 'active' : ''}" data-cat="bug">
                  <span class="gw-feedback-type-icon">🐞</span>
                  <span>Bug Report</span>
                </button>
                <button type="button" class="gw-feedback-type-btn ${this.feedbackCategory === 'feature' ? 'active' : ''}" data-cat="feature">
                  <span class="gw-feedback-type-icon">✨</span>
                  <span>Feature Request</span>
                </button>
                <button type="button" class="gw-feedback-type-btn ${this.feedbackCategory === 'feedback' ? 'active' : ''}" data-cat="feedback">
                  <span class="gw-feedback-type-icon">💬</span>
                  <span>General Feedback</span>
                </button>
              </div>
            </div>

            <div class="gw-feedback-field">
              <label for="gw-feedback-subject" class="gw-feedback-label">Subject</label>
              <input
                type="text"
                id="gw-feedback-subject"
                class="gw-feedback-input"
                maxlength="120"
                placeholder="${this.escapeHtml(subjectPlaceholder)}"
                value="${this.escapeHtml(this.feedbackSubject)}"
                ${this.feedbackSubmitting ? 'disabled' : ''}
              />
            </div>

            <div class="gw-feedback-field">
              <div class="gw-feedback-label-row">
                <label for="gw-feedback-message" class="gw-feedback-label">Details</label>
                <span id="gw-feedback-char-count" class="gw-feedback-char-count">${this.feedbackMessage.length} / 2000</span>
              </div>
              <textarea
                id="gw-feedback-message"
                class="gw-feedback-textarea"
                rows="5"
                maxlength="2000"
                placeholder="${this.escapeHtml(messagePlaceholder)}"
                ${this.feedbackSubmitting ? 'disabled' : ''}
              >${this.escapeHtml(this.feedbackMessage)}</textarea>
            </div>

            <!-- Hidden honeypot field -->
            <input type="text" id="gw-feedback-hp" name="website_url" tabindex="-1" autocomplete="off" style="display:none !important;" />

            <div class="gw-alert-item" style="padding: 10px 14px;">
              <div class="gw-alert-info">
                <span class="gw-alert-icon">🧭</span>
                <div>
                  <h4 class="gw-alert-title" style="font-size: 0.84rem;">Include Map & Layer Diagnostics</h4>
                  <p class="gw-alert-desc">Attaches your active activity, enabled weather layers, selected model, and map coordinates to help diagnose issues faster.</p>
                </div>
              </div>
              <label class="gw-switch">
                <input type="checkbox" id="gw-feedback-diag-toggle" ${this.feedbackIncludeDiagnostics ? 'checked' : ''} ${this.feedbackSubmitting ? 'disabled' : ''}>
                <span class="gw-slider"></span>
              </label>
            </div>

            <div class="gw-feedback-footer-row">
              <span class="gw-feedback-reply-note">
                Reports are delivered directly to SkySkout Support
              </span>
              <button
                type="button"
                id="gw-feedback-submit-btn"
                class="gw-auth-btn-primary gw-feedback-submit-btn"
                ${this.feedbackSubmitting ? 'disabled' : ''}
              >
                <span>${this.feedbackSubmitting ? 'Sending...' : 'Send Report'}</span>
              </button>
            </div>
          </div>
        </div>
      `;
    }

    // Default: 'account'
    const sub = store.getState().subscription;
    const status = sub?.status || 'none';
    const plan = sub?.plan;

    let planName = 'SkySkout Explorer (Free)';
    let badgeClass = 'gw-sub-badge-free';
    let badgeLabel = 'Free Base Maps';

    if (status === 'active') {
      badgeClass = 'gw-sub-badge-active';
      badgeLabel = 'Pro Active';
      if (plan === '12_month') planName = 'SkySkout Pro (Annual Pass)';
      else if (plan === '6_month') planName = 'SkySkout Pro (Seasonal Active Pass)';
      else if (plan === '1_month') planName = 'SkySkout Pro (Monthly Pass)';
      else planName = 'SkySkout Pro';
    } else if (status === 'trialing') {
      badgeClass = 'gw-sub-badge-trial';
      badgeLabel = '14-Day Free Trial';
      if (plan === '12_month') planName = 'SkySkout Pro (Annual - Free Trial)';
      else if (plan === '6_month') planName = 'SkySkout Pro (Seasonal - Free Trial)';
      else if (plan === '1_month') planName = 'SkySkout Pro (Monthly - Free Trial)';
      else planName = 'SkySkout Pro (Free Trial)';
    } else if (status === 'comped') {
      badgeClass = 'gw-sub-badge-active';
      badgeLabel = 'VIP / Lifetime';
      planName = 'SkySkout Pro (Complimentary Lifetime)';
    }

    const formatDate = (epochSec?: number | null) => {
      if (!epochSec) return 'None';
      try {
        return new Date(epochSec * 1000).toLocaleDateString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      } catch {
        return 'Active';
      }
    };

    return `
      <div class="gw-profile-tab-section">
        <div class="gw-profile-card">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px;">
            <h3 class="gw-profile-card-heading" style="margin: 0;">Subscription & Plan</h3>
            <span class="gw-sub-badge ${badgeClass}">${badgeLabel}</span>
          </div>

          <div class="gw-profile-status-row">
            <span class="gw-profile-status-label">Current Plan</span>
            <span class="gw-profile-status-val" style="font-weight: 600;">${planName}</span>
          </div>

          <div class="gw-profile-status-row">
            <span class="gw-profile-status-label">Atmospheric Forecast Layers</span>
            <span class="gw-profile-status-val" style="color: ${sub?.hasAccess ? '#10b981' : '#f59e0b'};">
              ${sub?.hasAccess ? '✓ Full Pro Access' : 'Base Maps Only'}
            </span>
          </div>

          ${status === 'trialing' && sub?.trialEnds ? `
            <div class="gw-profile-status-row">
              <span class="gw-profile-status-label">Trial Expiration</span>
              <span class="gw-profile-status-val" style="color: #60a5fa;">${formatDate(sub.trialEnds)}</span>
            </div>
          ` : ''}

          ${status === 'active' && sub?.currentPeriodEnd ? `
            <div class="gw-profile-status-row">
              <span class="gw-profile-status-label">Next Renewal Date</span>
              <span class="gw-profile-status-val">${formatDate(sub.currentPeriodEnd)}</span>
            </div>
          ` : ''}

          <div style="margin-top: 14px;">
            ${(status === 'active' || status === 'trialing') ? `
              <button id="gw-profile-billing-portal-btn" class="gw-profile-btn-action" style="width: 100%; justify-content: center;">
                💳 Manage Subscription & Billing (Stripe)
              </button>
            ` : `
              <div class="gw-profile-plan-selection">
                <div class="gw-profile-plan-intro">
                  <h4 style="margin: 0 0 4px; font-size: 14px; color: #f8fafc;">Choose a Plan — 14-Day Free Trial Included:</h4>
                  <p style="margin: 0 0 10px; font-size: 12px; color: #94a3b8;">You will not be charged until the 14-day trial ends. Cancel anytime in Stripe.</p>
                </div>

                <div class="gw-profile-plan-list">
                  <div class="gw-profile-plan-item featured">
                    <div class="gw-profile-plan-item-info">
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <span class="gw-profile-plan-item-name">Seasonal Active Pass (6 Months)</span>
                        <span class="gw-pricing-ribbon-inline">Most Popular</span>
                      </div>
                      <span class="gw-profile-plan-item-price">$24.99 / 6 mo (Save 16%) • 14 Days Free</span>
                    </div>
                    <button id="gw-profile-checkout-6mo" class="gw-pricing-btn gw-pricing-btn-highlight" style="width: auto; padding: 8px 16px; font-size: 12px; white-space: nowrap;">
                      Start 14-Day Trial
                    </button>
                  </div>

                  <div class="gw-profile-plan-item">
                    <div class="gw-profile-plan-item-info">
                      <div style="display: flex; align-items: center; gap: 8px;">
                        <span class="gw-profile-plan-item-name">Annual Pro (12 Months)</span>
                        <span class="gw-pricing-badge-inline">Best Value</span>
                      </div>
                      <span class="gw-profile-plan-item-price">$39.99 / year (Save 33%) • 14 Days Free</span>
                    </div>
                    <button id="gw-profile-checkout-12mo" class="gw-pricing-btn" style="width: auto; padding: 8px 16px; font-size: 12px; white-space: nowrap;">
                      Start 14-Day Trial
                    </button>
                  </div>

                  <div class="gw-profile-plan-item">
                    <div class="gw-profile-plan-item-info">
                      <span class="gw-profile-plan-item-name">Monthly Pass</span>
                      <span class="gw-profile-plan-item-price">$4.99 / month • 14 Days Free</span>
                    </div>
                    <button id="gw-profile-checkout-1mo" class="gw-pricing-btn" style="width: auto; padding: 8px 16px; font-size: 12px; white-space: nowrap;">
                      Start 14-Day Trial
                    </button>
                  </div>
                </div>
              </div>
            `}
          </div>
        </div>

        <!-- UI Workspace & Layout Persistence Card -->
        <div class="gw-profile-card" style="margin-top: 14px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
            <h3 class="gw-profile-card-heading" style="margin: 0;">UI Workspace & Layout</h3>
            <span class="gw-sub-badge" style="background: rgba(56, 189, 248, 0.15); color: #38bdf8; border: 1px solid rgba(56, 189, 248, 0.3);">
              Persistent
            </span>
          </div>
          <p style="font-size: 12px; color: #94a3b8; margin: 0 0 12px 0;">
            Customize and save your workspace preferences across desktop and tablet sessions, including drawer docking mode, panel coordinates, and forecast timeline horizon.
          </p>

          <div class="gw-profile-status-row">
            <span class="gw-profile-status-label">Drawer Dock Mode</span>
            <span class="gw-profile-status-val" style="text-transform: capitalize;">
              ${store.getState().drawerPosition === 'right' ? 'Right-Side Drawer' : 'Bottom Drawer'}
            </span>
          </div>

          <div class="gw-profile-status-row">
            <span class="gw-profile-status-label">Forecast Timeline</span>
            <span class="gw-profile-status-val">
              ${store.getState().timelineHorizon === 10 ? '10-Day Extended (240h)' : '5-Day Standard (120h)'}
            </span>
          </div>

          <div class="gw-profile-status-row">
            <span class="gw-profile-status-label">Legends Panel Position</span>
            <span class="gw-profile-status-val">
              ${(document.getElementById('map-legend-overlay') as any)?._isCustomPositioned ? 'Custom Repositioned' : 'Auto Smart-Anchored'}
            </span>
          </div>

          <div class="gw-profile-status-row" style="align-items: center;">
            <span class="gw-profile-status-label">Interface Font Scaling</span>
            <div class="gw-profile-font-size-toggle" style="display: flex; gap: 4px; background: rgba(0, 0, 0, 0.4); padding: 2px; border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.1);">
              <button type="button" class="gw-font-scale-btn ${store.getState().uiFontSize === 'compact' ? 'active' : ''}" data-size="compact" title="High-density layout (14px root)">
                Compact
              </button>
              <button type="button" class="gw-font-scale-btn ${store.getState().uiFontSize === 'standard' ? 'active' : ''}" data-size="standard" title="Standard layout (16px root)">
                Standard
              </button>
              <button type="button" class="gw-font-scale-btn ${store.getState().uiFontSize === 'large' ? 'active' : ''}" data-size="large" title="Large legible layout (18px root)">
                Large
              </button>
            </div>
          </div>

          <div class="gw-profile-status-row" style="align-items: center;">
            <span class="gw-profile-status-label">Measurement Units</span>
            <div class="gw-profile-units-toggle" style="display: flex; gap: 4px; background: rgba(0, 0, 0, 0.4); padding: 2px; border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.1);">
              <button type="button" class="gw-units-scale-btn ${store.getState().units !== 'metric' ? 'active' : ''}" data-units="imperial" title="Imperial units (°F, ft, in, mph)">
                Imperial (°F, ft)
              </button>
              <button type="button" class="gw-units-scale-btn ${store.getState().units === 'metric' ? 'active' : ''}" data-units="metric" title="Metric units (°C, m, mm, km/h)">
                Metric (°C, m)
              </button>
            </div>
          </div>

          <div class="gw-profile-status-row" style="align-items: center;">
            <span class="gw-profile-status-label">UI Theme</span>
            <div class="gw-profile-theme-toggle" style="display: flex; gap: 4px; background: rgba(0, 0, 0, 0.4); padding: 2px; border-radius: 6px; border: 1px solid rgba(255, 255, 255, 0.1); flex-wrap: wrap;">
              ${THEMES_LIST.map(t => {
      const isActive = themeService.getTheme() === t.id;
      return `
                  <button type="button" class="gw-theme-scale-btn ${isActive ? 'active' : ''}" data-theme-id="${t.id}" title="${t.description}">
                    <span>${t.icon}</span> <span>${t.name}</span>
                  </button>
                `;
    }).join('')}
            </div>
          </div>

          <div style="display: flex; gap: 8px; margin-top: 14px; flex-wrap: wrap;">
            <button id="gw-btn-save-ui-layout" class="gw-profile-btn-action" style="flex: 1; justify-content: center; min-width: 160px;">
              💾 Save Current Workspace
            </button>
            <button id="gw-btn-reset-ui-layout" style="background: rgba(239, 68, 68, 0.15); border: 1px solid rgba(239, 68, 68, 0.35); color: #fca5a5; padding: 6px 14px; border-radius: 6px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s; white-space: nowrap;">
              ↺ Reset to Defaults
            </button>
          </div>
          <div id="gw-layout-save-feedback" style="display: none; margin-top: 8px; font-size: 12px; color: #34d399; text-align: center;"></div>
        </div>
      </div>
    `;
  }

  private renderPerformanceTab(): string {
    const state = store.getState();
    const windThrottle = state.windAnimationThrottle || 'unlimited';
    const windBatching = state.windColorBatching || 'high';
    const windRes = state.windCanvasResolution || 'high';
    const contourSpacing = state.contourSpacing || 'low';

    return `
      <div class="gw-profile-tab-section">
        <div class="gw-profile-section-header">
          <div>
            <h3 class="gw-profile-section-title">Graphics & Performance Tuning</h3>
            <p class="gw-profile-section-desc">Optimize rendering framerate, GPU memory bandwidth, and particle animation overhead for your device hardware.</p>
          </div>
        </div>

        <div class="gw-alerts-list">
          <!-- Wind Animation Throttle -->
          <div class="gw-alert-item">
            <div class="gw-alert-info">
              <span class="gw-alert-icon">⏱️</span>
              <div>
                <h4 class="gw-alert-title">Wind Animation Throttling</h4>
                <p class="gw-alert-desc">Caps wind particle updates to 30 FPS to reduce CPU/GPU power consumption and battery usage on laptops, or allow full refresh rate (60Hz–144Hz).</p>
              </div>
            </div>
            <div>
              <select id="gw-perf-wind-throttle" class="gw-select-input" style="padding: 6px 10px; background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px; color: #f8fafc; font-size: 0.82rem; cursor: pointer;">
                <option value="unlimited" ${windThrottle === 'unlimited' ? 'selected' : ''}>Full Display Rate (Unlimited)</option>
                <option value="30fps" ${windThrottle === '30fps' ? 'selected' : ''}>30 FPS (Power Saver)</option>
              </select>
            </div>
          </div>

          <!-- Color-Bucketed Batching -->
          <div class="gw-alert-item">
            <div class="gw-alert-info">
              <span class="gw-alert-icon">🎨</span>
              <div>
                <h4 class="gw-alert-title">Wind Color-Bucketed Batching</h4>
                <p class="gw-alert-desc">Batches particle stream segments by speed into color buckets to eliminate thousands of canvas draw calls per frame.</p>
              </div>
            </div>
            <div>
              <select id="gw-perf-wind-batching" class="gw-select-input" style="padding: 6px 10px; background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px; color: #f8fafc; font-size: 0.82rem; cursor: pointer;">
                <option value="low" ${windBatching === 'low' ? 'selected' : ''}>Low (8 Colors - Fastest)</option>
                <option value="med" ${windBatching === 'med' ? 'selected' : ''}>Medium (16 Colors - Balanced)</option>
                <option value="high" ${windBatching === 'high' ? 'selected' : ''}>High (64 Colors - High Fidelity)</option>
              </select>
            </div>
          </div>

          <!-- Wind Canvas Internal Resolution -->
          <div class="gw-alert-item">
            <div class="gw-alert-info">
              <span class="gw-alert-icon">🖥️</span>
              <div>
                <h4 class="gw-alert-title">Wind Canvas Resolution</h4>
                <p class="gw-alert-desc">Scales internal offscreen particle buffer resolution relative to viewport. Lower values significantly reduce GPU fillrate and texture upload bandwidth.</p>
              </div>
            </div>
            <div>
              <select id="gw-perf-wind-resolution" class="gw-select-input" style="padding: 6px 10px; background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px; color: #f8fafc; font-size: 0.82rem; cursor: pointer;">
                <option value="low" ${windRes === 'low' ? 'selected' : ''}>Low (0.50x - Minimal Fillrate)</option>
                <option value="med" ${windRes === 'med' ? 'selected' : ''}>Medium (0.75x - Balanced)</option>
                <option value="high" ${windRes === 'high' ? 'selected' : ''}>High (1.00x - Native Display)</option>
              </select>
            </div>
          </div>

          <!-- Temperature Contour Label Spacing -->
          <div class="gw-alert-item">
            <div class="gw-alert-info">
              <span class="gw-alert-icon">📈</span>
              <div>
                <h4 class="gw-alert-title">Temperature Contour Spacing</h4>
                <p class="gw-alert-desc">Controls label frequency along topographic temperature contour lines. High spacing reduces label collisions and symbol rendering overhead.</p>
              </div>
            </div>
            <div>
              <select id="gw-perf-contour-spacing" class="gw-select-input" style="padding: 6px 10px; background: rgba(15, 23, 42, 0.8); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 8px; color: #f8fafc; font-size: 0.82rem; cursor: pointer;">
                <option value="low" ${contourSpacing === 'low' ? 'selected' : ''}>Standard (220px - Dense Labels)</option>
                <option value="high" ${contourSpacing === 'high' ? 'selected' : ''}>Sparse (450px - Lightweight)</option>
              </select>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private renderActivitiesTab(): string {
    const state = store.getState();
    const sub = state.subscription;
    const isPro = sub && (sub.status === 'active' || sub.status === 'trialing' || sub.plan === 'comped');

    if (!isPro) {
      return this.renderProUpgradePreview();
    }

    if (this.isCreatingCustomActivity) {
      return this.renderCustomActivityEditor();
    }

    const allActivities = getAllActivities(state.customActivities);
    const enabledIds = state.enabledActivities && state.enabledActivities.length > 0
      ? state.enabledActivities
      : ['standard_forecast', 'hiking', 'photography', 'stargazing', 'winter_sports', 'water_sports', 'advanced'];

    const availableCanned = Object.values(CANNED_ACTIVITIES_CONFIG).filter(
      (act) => !enabledIds.includes(act.id)
    );

    const customList = state.customActivities || [];

    return `
      <div class="gw-profile-tab-section">
        <div class="gw-profile-section-header">
          <div>
            <h3 class="gw-profile-section-title">My Activity Personas</h3>
            <p class="gw-profile-section-desc">Curate which activities appear in your drawer, or create up to 5 custom-tailored weather workspaces.</p>
          </div>
          ${customList.length < 5 ? `
            <button id="gw-btn-start-custom-act" class="gw-profile-btn-action">
              <span>+ Create Custom Activity</span>
            </button>
          ` : `
            <span class="gw-custom-cap-tag">Max 5/5 Custom Activities</span>
          `}
        </div>

        <!-- Section 1: Active in Drawer -->
        <div class="gw-profile-card">
          <div class="gw-activities-card-header">
            <h4 class="gw-profile-card-heading" style="margin: 0;">Active in Drawer (${enabledIds.length})</h4>
            <span class="gw-card-subtext">Drag or use arrows to change order in the drawer</span>
          </div>
          <div class="gw-activities-list">
            ${enabledIds.map((id, index) => {
      const act = allActivities[id];
      if (!act) return '';
      const isFirst = index === 0;
      const isLast = index === enabledIds.length - 1;
      const isCustom = !!act.isCustom;
      return `
                <div class="gw-activity-row" data-id="${act.id}" data-index="${index}" draggable="true">
                  <div class="gw-activity-row-left">
                    <div class="gw-activity-drag-handle" title="Drag to reorder" draggable="false">⋮⋮</div>
                    <div class="gw-activity-reorder-group" draggable="false">
                      <button class="gw-reorder-btn gw-btn-up" data-id="${act.id}" ${isFirst ? 'disabled' : ''} title="Move Up" draggable="false">▲</button>
                      <button class="gw-reorder-btn gw-btn-down" data-id="${act.id}" ${isLast ? 'disabled' : ''} title="Move Down" draggable="false">▼</button>
                    </div>
                    <span class="gw-act-icon-lg">${act.icon}</span>
                    <div class="gw-act-row-details">
                      <div class="gw-act-row-title-bar">
                        <span class="gw-act-row-title">${this.escapeHtml(act.title)}</span>
                        <span class="gw-act-badge ${isCustom ? 'custom' : 'canned'}">${isCustom ? 'Custom' : 'Default'}</span>
                      </div>
                      <span class="gw-act-row-desc">${this.escapeHtml(act.description)}</span>
                    </div>
                  </div>
                  <div class="gw-activity-row-actions" draggable="false">
                    ${isCustom ? `
                      <button class="gw-btn-edit-act" data-id="${act.id}" title="Edit Layers & Settings" draggable="false">✏️</button>
                      <button class="gw-btn-del-act" data-id="${act.id}" title="Delete Custom Activity" draggable="false">🗑️</button>
                    ` : ''}
                    <button class="gw-btn-remove-act" data-id="${act.id}" title="Remove from Drawer" draggable="false" ${enabledIds.length <= 1 ? 'disabled' : ''}>✕</button>
                  </div>
                </div>
              `;
    }).join('')}
          </div>
        </div>

        <!-- Section 2: Available Canned Personas -->
        ${availableCanned.length > 0 ? `
          <div class="gw-profile-card">
            <div class="gw-activities-card-header">
              <h4 class="gw-profile-card-heading" style="margin: 0;">Available Canned Personas (${availableCanned.length})</h4>
              <span class="gw-card-subtext">Click to add to your drawer navigation</span>
            </div>
            <div class="gw-library-grid">
              ${availableCanned.map((act) => `
                <div class="gw-library-card">
                  <div class="gw-library-card-info">
                    <span class="gw-act-icon-lg">${act.icon}</span>
                    <div>
                      <h5 class="gw-library-card-title">${this.escapeHtml(act.title)}</h5>
                      <p class="gw-library-card-desc">${this.escapeHtml(act.description)}</p>
                    </div>
                  </div>
                  <button class="gw-btn-add-canned" data-id="${act.id}">+ Add to Drawer</button>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}
      </div>
    `;
  }

  private renderProUpgradePreview(): string {
    return `
      <div class="gw-profile-tab-section">
        <div class="gw-pro-feature-banner">
          <div class="gw-pro-feature-header">
            <span class="gw-pro-crown-icon">👑</span>
            <div>
              <h3 class="gw-pro-feature-title">Personalized Activities & Custom Personas</h3>
              <p class="gw-pro-feature-subtitle">Curate your drawer, unlock specialized activities, and build your own tailored weather workspaces with SkySkout Pro.</p>
            </div>
          </div>

          <div class="gw-pro-perks-grid">
            <div class="gw-pro-perk-card">
              <span class="gw-pro-perk-icon">🎯</span>
              <h4>Drawer Curation</h4>
              <p>Add, remove, and reorder personas in your drawer so the activities you care about are always front and center.</p>
            </div>
            <div class="gw-pro-perk-card">
              <span class="gw-pro-perk-icon">🍄</span>
              <h4>Specialized Canned Personas</h4>
              <p>Unlock Wild Mushroom Foraging, Hunting & Wildlife, Trail Running & Cycling, and Backcountry Aviation & Drone personas.</p>
            </div>
            <div class="gw-pro-perk-card">
              <span class="gw-pro-perk-icon">⚡</span>
              <h4>Up to 5 Custom Activities</h4>
              <p>Build custom activities from scratch. Select which layers show up as prominent Hero Cards and which as Quick-Toggle Pills.</p>
            </div>
          </div>

          <div class="gw-pro-preview-locked-list">
            <h4 style="font-size: 13px; color: #cbd5e1; margin-bottom: 8px;">Exclusive Pro Activity Personas:</h4>
            <div class="gw-locked-personas-row">
              <div class="gw-locked-persona-chip"><span class="gw-locked-chip-icon">🍄</span> Mushroom & Berry Foraging <span class="gw-pro-chip-tag">PRO</span></div>
              <div class="gw-locked-persona-chip"><span class="gw-locked-chip-icon">🏹</span> Hunting & Wildlife <span class="gw-pro-chip-tag">PRO</span></div>
              <div class="gw-locked-persona-chip"><span class="gw-locked-chip-icon">🏃</span> Trail Running & Cycling <span class="gw-pro-chip-tag">PRO</span></div>
              <div class="gw-locked-persona-chip"><span class="gw-locked-chip-icon">🛩️</span> Backcountry Aviation & Drone <span class="gw-pro-chip-tag">PRO</span></div>
            </div>
          </div>

          <div style="margin-top: 18px; display: flex; justify-content: flex-end;">
            <button id="gw-pro-upgrade-btn" class="gw-pricing-btn gw-pricing-btn-highlight" style="width: auto; padding: 10px 24px;">
              Explore SkySkout Pro Plans (14-Day Free Trial)
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCustomActivityEditor(): string {
    const isEdit = !!this.editingCustomActivityId;
    const emojiPresets = ['🏔️', '🥾', '📷', '🌌', '❄️', '🌊', '🍄', '🏹', '🏃', '🚵', '🚴', '🛩️', '⛵', '⛺', '🧗', '🎣', '🌲', '🍂', '🌋'];
    const query = this.layerSearchQuery.toLowerCase().trim();

    return `
      <div class="gw-profile-tab-section gw-custom-editor-container">
        <div class="gw-profile-section-header" style="align-items: center;">
          <div>
            <h3 class="gw-profile-section-title">${isEdit ? 'Edit Custom Activity' : 'Create Custom Activity'}</h3>
            <p class="gw-profile-section-desc">Design a customized activity persona with custom Hero Cards and Quick-Toggle pills.</p>
          </div>
          <button id="gw-btn-cancel-custom-act" class="gw-profile-btn-secondary" style="padding: 6px 14px; font-size: 12px;">Cancel</button>
        </div>

        <div class="gw-profile-card">
          <div class="gw-custom-settings-grid">
            <div class="gw-input-group">
              <label class="gw-input-label">Activity Name *</label>
              <input type="text" id="gw-custom-title-input" class="gw-text-input" placeholder="e.g. Cascade Alpine Scouting" value="${this.escapeHtml(this.customTitle)}" maxlength="40" />
            </div>

            <div class="gw-input-group">
              <label class="gw-input-label">Default Base Map</label>
              <select id="gw-custom-basemap-select" class="gw-select-input">
                <option value="road" ${this.customBasemap === 'road' || this.customBasemap === 'terrain-mapterhorn' ? 'selected' : ''}>Skyskout - OSM</option>
                <option value="usgs-topo" ${this.customBasemap === 'usgs-topo' ? 'selected' : ''}>USGS Topo Map</option>
                <option value="usgs-naip" ${this.customBasemap === 'usgs-naip' ? 'selected' : ''}>USGS Satellite</option>
              </select>
            </div>
          </div>

          <div class="gw-input-group" style="margin-top: 14px;">
            <label class="gw-input-label">Activity Icon</label>
            <div class="gw-emoji-picker-row">
              ${emojiPresets.map(emoji => `
                <button type="button" class="gw-emoji-chip ${this.customIcon === emoji ? 'selected' : ''}" data-emoji="${emoji}">${emoji}</button>
              `).join('')}
              <input type="text" id="gw-custom-icon-input" class="gw-text-input gw-custom-icon-box" value="${this.customIcon}" maxlength="4" placeholder="Custom" />
            </div>
          </div>
        </div>

        <!-- 3-State Categorized Layer Picker -->
        <div class="gw-profile-card">
          <div class="gw-custom-layers-header">
            <div>
              <h4 class="gw-profile-card-heading" style="margin: 0 0 4px 0;">Configure Layers</h4>
              <span class="gw-card-subtext">Select which layers appear as prominent Hero Cards (Primary) or Quick-Toggle Pills (Secondary)</span>
            </div>
            <div class="gw-layer-counters-pill">
              <span class="gw-counter-primary">⭐ ${this.tempPrimaryLayers.size} Primary Hero</span>
              <span class="gw-counter-secondary">📍 ${this.tempSecondaryLayers.size} Secondary Pills</span>
            </div>
          </div>

          <div class="gw-custom-layers-toolbar">
            <input type="text" id="gw-layer-filter-input" class="gw-text-input" placeholder="🔍 Filter weather layers..." value="${this.escapeHtml(this.layerSearchQuery)}" />
            <div class="gw-accordion-toggle-group">
              <button type="button" id="gw-btn-expand-all" class="gw-toolbar-mini-btn" title="Expand all categories">Expand All</button>
              <button type="button" id="gw-btn-collapse-all" class="gw-toolbar-mini-btn" title="Collapse all categories">Collapse All</button>
            </div>
          </div>

          <div class="gw-layer-picker-categories">
            ${LAYER_CATEGORIES.map(cat => {
      const catLayers = Object.values(MASTER_LAYER_CATALOG).filter(
        (l: any) => l.category === cat.id && (!query || l.label.toLowerCase().includes(query) || l.id.toLowerCase().includes(query))
      );
      if (catLayers.length === 0) return '';
      const isCollapsed = !query && this.collapsedCategories.has(cat.id);
      const activeInCatCount = catLayers.filter((l: any) => this.tempPrimaryLayers.has(l.id) || this.tempSecondaryLayers.has(l.id)).length;
      return `
                <div class="gw-picker-category-block ${isCollapsed ? 'collapsed' : ''}" data-cat-id="${cat.id}">
                  <div class="gw-picker-category-title" data-cat-id="${cat.id}" role="button" tabindex="0" title="Click to collapse or expand">
                    <div class="gw-picker-cat-title-left">
                      <span class="gw-picker-cat-chevron">${isCollapsed ? '▶' : '▼'}</span>
                      <span class="gw-picker-cat-icon">${cat.icon}</span>
                      <span class="gw-picker-cat-name">${cat.title}</span>
                    </div>
                    <div class="gw-picker-cat-title-right">
                      ${activeInCatCount > 0 ? `<span class="gw-cat-active-pill">${activeInCatCount} configured</span>` : ''}
                      <span class="gw-cat-total-count">${catLayers.length} layers</span>
                    </div>
                  </div>
                  <div class="gw-picker-layers-list">
                    ${catLayers.map((l: any) => {
        const isPrimary = this.tempPrimaryLayers.has(l.id);
        const isSecondary = this.tempSecondaryLayers.has(l.id);
        const isOff = !isPrimary && !isSecondary;
        return `
                        <div class="gw-picker-layer-row" data-id="${l.id}">
                          <div class="gw-picker-layer-info">
                            <span class="gw-picker-layer-icon">${l.icon || '📍'}</span>
                            <div class="gw-picker-layer-text">
                              <span class="gw-picker-layer-name">${this.escapeHtml(l.label)}</span>
                              ${l.description ? `<span class="gw-picker-layer-desc">${this.escapeHtml(l.description)}</span>` : ''}
                            </div>
                          </div>
                          <div class="gw-segment-btn-group" data-id="${l.id}" data-cat-id="${cat.id}">
                            <button type="button" class="gw-segment-btn ${isOff ? 'active off' : ''}" data-state="off">Off</button>
                            <button type="button" class="gw-segment-btn ${isSecondary ? 'active secondary' : ''}" data-state="secondary">Secondary</button>
                            <button type="button" class="gw-segment-btn ${isPrimary ? 'active primary' : ''}" data-state="primary">Primary (Hero)</button>
                          </div>
                        </div>
                      `;
      }).join('')}
                  </div>
                </div>
              `;
    }).join('')}
          </div>
        </div>

        <!-- Action Footer -->
        <div style="display: flex; justify-content: flex-end; gap: 10px; margin-top: 10px;">
          <button id="gw-btn-cancel-custom-act-bottom" class="gw-profile-btn-secondary">Cancel</button>
          <button id="gw-btn-save-custom-act" class="gw-auth-btn-primary" style="width: auto; padding: 10px 24px;">
            <span>${isEdit ? 'Save Changes' : 'Create Activity'}</span>
          </button>
        </div>
      </div>
    `;
  }

  private bindEvents(): void {
    if (!this.container) return;

    const closeBtn = this.container.querySelector('#gw-profile-close-btn') as HTMLElement;
    if (closeBtn) closeBtn.onclick = () => this.close();

    const doneBtn = this.container.querySelector('#gw-profile-done-btn') as HTMLElement;
    if (doneBtn) doneBtn.onclick = () => this.close();

    // Click outside backdrop to close
    this.container.onclick = (e) => {
      if (e.target === this.container) {
        this.close();
      }
    };

    // Tab buttons
    const tabAccount = this.container.querySelector('#gw-tab-btn-account') as HTMLElement;
    const tabActivities = this.container.querySelector('#gw-tab-btn-activities') as HTMLElement;
    const tabLocations = this.container.querySelector('#gw-tab-btn-locations') as HTMLElement;
    const tabFeedback = this.container.querySelector('#gw-tab-btn-feedback') as HTMLElement;
    const tabPerf = this.container.querySelector('#gw-tab-btn-performance') as HTMLElement;

    if (tabAccount) tabAccount.onclick = () => { this.activeTab = 'account'; this.render(); };
    if (tabActivities) tabActivities.onclick = () => { this.activeTab = 'activities'; this.render(); };
    if (tabLocations) tabLocations.onclick = () => { this.activeTab = 'locations'; this.render(); };
    if (tabFeedback) tabFeedback.onclick = () => { this.activeTab = 'feedback'; this.feedbackStatus = null; this.render(); };
    if (tabPerf) tabPerf.onclick = () => { this.activeTab = 'performance'; this.render(); };

    // Performance Select Handlers
    const perfWindThrottle = this.container.querySelector('#gw-perf-wind-throttle') as HTMLSelectElement;
    if (perfWindThrottle) {
      perfWindThrottle.onchange = () => {
        store.update({ windAnimationThrottle: perfWindThrottle.value as '30fps' | 'unlimited' });
      };
    }

    const perfWindBatching = this.container.querySelector('#gw-perf-wind-batching') as HTMLSelectElement;
    if (perfWindBatching) {
      perfWindBatching.onchange = () => {
        store.update({ windColorBatching: perfWindBatching.value as 'low' | 'med' | 'high' });
      };
    }

    const perfWindRes = this.container.querySelector('#gw-perf-wind-resolution') as HTMLSelectElement;
    if (perfWindRes) {
      perfWindRes.onchange = () => {
        store.update({ windCanvasResolution: perfWindRes.value as 'low' | 'med' | 'high' });
      };
    }

    const perfContourSpacing = this.container.querySelector('#gw-perf-contour-spacing') as HTMLSelectElement;
    if (perfContourSpacing) {
      perfContourSpacing.onchange = () => {
        store.update({ contourSpacing: perfContourSpacing.value as 'low' | 'high' });
      };
    }

    // Sign out button
    const signoutBtn = this.container.querySelector('#gw-profile-signout-btn') as HTMLElement;
    if (signoutBtn) {
      signoutBtn.onclick = async () => {
        await authService.signOut();
        this.close();
      };
    }

    // Pro upgrade button
    const proUpgradeBtn = this.container.querySelector('#gw-pro-upgrade-btn') as HTMLElement;
    if (proUpgradeBtn) {
      proUpgradeBtn.onclick = () => {
        this.activeTab = 'account';
        this.render();
      };
    }

    // Manage Billing Portal button
    const portalBtn = this.container.querySelector('#gw-profile-billing-portal-btn') as HTMLButtonElement;
    if (portalBtn) {
      portalBtn.onclick = async () => {
        portalBtn.disabled = true;
        portalBtn.innerText = 'Redirecting to Stripe...';
        const res = await authService.openBillingPortal();
        if (!res.success) {
          alert(res.error || 'Could not open billing portal.');
          portalBtn.disabled = false;
          portalBtn.innerText = '💳 Manage Subscription & Billing (Stripe)';
        }
      };
    }

    // Plan Checkout Buttons
    const handleCheckout = async (plan: '1_month' | '6_month' | '12_month', btn: HTMLButtonElement) => {
      btn.disabled = true;
      btn.innerText = 'Redirecting to Stripe...';
      const res = await authService.createCheckoutSession(plan);
      if (!res.success) {
        alert(res.error || 'Failed to redirect to Stripe checkout.');
        btn.disabled = false;
        btn.innerText = 'Start 14-Day Trial';
      }
    };

    const btn1mo = this.container.querySelector('#gw-profile-checkout-1mo') as HTMLButtonElement;
    const btn6mo = this.container.querySelector('#gw-profile-checkout-6mo') as HTMLButtonElement;
    const btn12mo = this.container.querySelector('#gw-profile-checkout-12mo') as HTMLButtonElement;

    if (btn1mo) btn1mo.onclick = () => handleCheckout('1_month', btn1mo);
    if (btn6mo) btn6mo.onclick = () => handleCheckout('6_month', btn6mo);
    if (btn12mo) btn12mo.onclick = () => handleCheckout('12_month', btn12mo);

    // Layout Persistence Handlers
    const saveLayoutBtn = this.container.querySelector('#gw-btn-save-ui-layout') as HTMLButtonElement | null;
    const resetLayoutBtn = this.container.querySelector('#gw-btn-reset-ui-layout') as HTMLButtonElement | null;
    const feedbackEl = this.container.querySelector('#gw-layout-save-feedback') as HTMLElement | null;

    if (saveLayoutBtn) {
      saveLayoutBtn.onclick = async () => {
        saveLayoutBtn.disabled = true;
        saveLayoutBtn.innerText = 'Saving...';
        const ok = await LayoutManager.saveLayout();
        saveLayoutBtn.disabled = false;
        saveLayoutBtn.innerText = ok ? '✓ Saved!' : 'Save Failed';
        if (feedbackEl) {
          feedbackEl.style.display = 'block';
          feedbackEl.style.color = ok ? '#34d399' : '#f87171';
          feedbackEl.textContent = ok ? 'Workspace layout preferences saved!' : 'Failed to save layout preferences.';
        }
        setTimeout(() => {
          if (this.container && this.activeTab === 'account') {
            this.render();
          }
        }, 1500);
      };
    }

    if (resetLayoutBtn) {
      resetLayoutBtn.onclick = async () => {
        if (!confirm('Reset UI workspace (drawer, panel positions, timeline horizon) to defaults?')) return;
        resetLayoutBtn.disabled = true;
        resetLayoutBtn.innerText = 'Resetting...';
        await LayoutManager.resetDefaultLayout();
        if (feedbackEl) {
          feedbackEl.style.display = 'block';
          feedbackEl.style.color = '#38bdf8';
          feedbackEl.textContent = 'Workspace reset to default settings.';
        }
        setTimeout(() => {
          if (this.container && this.activeTab === 'account') {
            this.render();
          }
        }, 1200);
      };
    }

    // UI Font Size Selection Buttons
    if (this.container) {
      this.container.querySelectorAll<HTMLButtonElement>('.gw-font-scale-btn').forEach(btn => {
        btn.onclick = () => {
          const newSize = btn.dataset.size as any;
          if (newSize && ['compact', 'standard', 'large'].includes(newSize)) {
            try {
              localStorage.setItem('gw_ui_font_size', newSize);
              document.documentElement.setAttribute('data-ui-font-size', newSize);
            } catch (_) { }
            store.update({ uiFontSize: newSize, isUiLayoutDirty: true });
            this.render();
          }
        };
      });

      // Measurement Units Selection Buttons
      this.container.querySelectorAll<HTMLButtonElement>('.gw-units-scale-btn').forEach(btn => {
        btn.onclick = () => {
          const newUnits = btn.dataset.units as 'imperial' | 'metric';
          if (newUnits && (newUnits === 'imperial' || newUnits === 'metric')) {
            try {
              localStorage.setItem('skyskout_units', newUnits);
            } catch (_) { }
            store.update({ units: newUnits, isUiLayoutDirty: true });
            if (typeof (window as any).__onUnitsChanged === 'function') {
              (window as any).__onUnitsChanged(newUnits);
            }
            this.render();
          }
        };
      });

      // UI Theme Selection Buttons
      this.container.querySelectorAll<HTMLButtonElement>('.gw-theme-scale-btn').forEach(btn => {
        btn.onclick = () => {
          const newTheme = btn.dataset.themeId as ThemeId;
          if (newTheme && ['dark', 'earth', 'paper', 'forest'].includes(newTheme)) {
            themeService.setTheme(newTheme);
            this.render();
          }
        };
      });
    }

    // --- Activities Tab Event Handlers ---
    if (this.activeTab === 'activities') {
      // Start create custom activity
      const startCustomBtn = this.container.querySelector('#gw-btn-start-custom-act') as HTMLElement;
      if (startCustomBtn) {
        startCustomBtn.onclick = () => {
          this.isCreatingCustomActivity = true;
          this.editingCustomActivityId = null;
          this.customTitle = '';
          this.customIcon = '🏔️';
          this.customBasemap = 'road';
          this.tempPrimaryLayers = new Set();
          this.tempSecondaryLayers = new Set();
          this.layerSearchQuery = '';
          this.render();
        };
      }

      // Cancel custom activity
      const cancelBtn1 = this.container.querySelector('#gw-btn-cancel-custom-act') as HTMLElement;
      const cancelBtn2 = this.container.querySelector('#gw-btn-cancel-custom-act-bottom') as HTMLElement;
      const handleCancel = () => {
        this.isCreatingCustomActivity = false;
        this.editingCustomActivityId = null;
        this.render();
      };
      if (cancelBtn1) cancelBtn1.onclick = handleCancel;
      if (cancelBtn2) cancelBtn2.onclick = handleCancel;

      // Custom Activity inputs
      const titleInput = this.container.querySelector('#gw-custom-title-input') as HTMLInputElement;
      if (titleInput) {
        titleInput.oninput = () => { this.customTitle = titleInput.value; };
      }

      const basemapSelect = this.container.querySelector('#gw-custom-basemap-select') as HTMLSelectElement;
      if (basemapSelect) {
        basemapSelect.onchange = () => { this.customBasemap = basemapSelect.value; };
      }

      const iconInput = this.container.querySelector('#gw-custom-icon-input') as HTMLInputElement;
      if (iconInput) {
        iconInput.oninput = () => {
          this.customIcon = iconInput.value || '📍';
          this.container?.querySelectorAll('.gw-emoji-chip').forEach(c => c.classList.remove('selected'));
        };
      }

      this.container.querySelectorAll('.gw-emoji-chip').forEach((chip) => {
        (chip as HTMLElement).onclick = () => {
          const emoji = chip.getAttribute('data-emoji') || '📍';
          this.customIcon = emoji;
          if (iconInput) iconInput.value = emoji;
          this.container?.querySelectorAll('.gw-emoji-chip').forEach(c => c.classList.remove('selected'));
          chip.classList.add('selected');
        };
      });

      // Layer search filter
      const filterInput = this.container.querySelector('#gw-layer-filter-input') as HTMLInputElement;
      if (filterInput) {
        filterInput.oninput = () => {
          this.layerSearchQuery = filterInput.value;
          const titleInput = this.container?.querySelector('#gw-custom-title-input') as HTMLInputElement;
          if (titleInput) this.customTitle = titleInput.value;
          const basemapSelect = this.container?.querySelector('#gw-custom-basemap-select') as HTMLSelectElement;
          if (basemapSelect) this.customBasemap = basemapSelect.value;

          this.render();
          const nextInput = this.container?.querySelector('#gw-layer-filter-input') as HTMLInputElement;
          if (nextInput) {
            nextInput.focus();
            nextInput.selectionStart = nextInput.selectionEnd = nextInput.value.length;
          }
        };
      }

      // Expand All categories
      const expandAllBtn = this.container.querySelector('#gw-btn-expand-all') as HTMLElement;
      if (expandAllBtn) {
        expandAllBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.collapsedCategories.clear();
          this.container?.querySelectorAll('.gw-picker-category-block').forEach((b) => {
            b.classList.remove('collapsed');
            const ch = b.querySelector('.gw-picker-cat-chevron');
            if (ch) ch.textContent = '▼';
          });
        };
      }

      // Collapse All categories
      const collapseAllBtn = this.container.querySelector('#gw-btn-collapse-all') as HTMLElement;
      if (collapseAllBtn) {
        collapseAllBtn.onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          LAYER_CATEGORIES.forEach(c => this.collapsedCategories.add(c.id));
          this.container?.querySelectorAll('.gw-picker-category-block').forEach((b) => {
            b.classList.add('collapsed');
            const ch = b.querySelector('.gw-picker-cat-chevron');
            if (ch) ch.textContent = '▶';
          });
        };
      }

      // Accordion Category Title Click
      this.container.querySelectorAll('.gw-picker-category-title').forEach((titleEl) => {
        (titleEl as HTMLElement).onclick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          const catId = titleEl.getAttribute('data-cat-id');
          if (!catId) return;
          const block = this.container?.querySelector(`.gw-picker-category-block[data-cat-id="${catId}"]`);
          if (!block) return;
          const chevron = block.querySelector('.gw-picker-cat-chevron');
          if (this.collapsedCategories.has(catId)) {
            this.collapsedCategories.delete(catId);
            block.classList.remove('collapsed');
            if (chevron) chevron.textContent = '▼';
          } else {
            this.collapsedCategories.add(catId);
            block.classList.add('collapsed');
            if (chevron) chevron.textContent = '▶';
          }
        };
      });

      // 3-state segment buttons
      this.container.querySelectorAll('.gw-segment-btn-group').forEach((group) => {
        const layerId = group.getAttribute('data-id');
        const catId = group.getAttribute('data-cat-id');
        if (!layerId) return;

        group.querySelectorAll('.gw-segment-btn').forEach((btn) => {
          (btn as HTMLElement).onclick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            const state = btn.getAttribute('data-state');
            if (state === 'off') {
              this.tempPrimaryLayers.delete(layerId);
              this.tempSecondaryLayers.delete(layerId);
            } else if (state === 'secondary') {
              this.tempPrimaryLayers.delete(layerId);
              this.tempSecondaryLayers.add(layerId);
            } else if (state === 'primary') {
              this.tempSecondaryLayers.delete(layerId);
              this.tempPrimaryLayers.add(layerId);
            }

            // Update button styles in group
            group.querySelectorAll('.gw-segment-btn').forEach(b => {
              b.classList.remove('active', 'off', 'secondary', 'primary');
            });
            btn.classList.add('active', state || 'off');

            // Update counter badges
            const primaryCountEl = this.container?.querySelector('.gw-counter-primary');
            const secondaryCountEl = this.container?.querySelector('.gw-counter-secondary');
            if (primaryCountEl) primaryCountEl.innerHTML = `⭐ ${this.tempPrimaryLayers.size} Primary Hero`;
            if (secondaryCountEl) secondaryCountEl.innerHTML = `📍 ${this.tempSecondaryLayers.size} Secondary Pills`;

            // Update category badge if present
            if (catId) {
              const catBlock = this.container?.querySelector(`.gw-picker-category-block[data-cat-id="${catId}"]`);
              if (catBlock) {
                const badgeContainer = catBlock.querySelector('.gw-picker-cat-title-right');
                const catLayers = Object.values(MASTER_LAYER_CATALOG).filter((l: any) => l.category === catId);
                const activeInCatCount = catLayers.filter((l: any) => this.tempPrimaryLayers.has(l.id) || this.tempSecondaryLayers.has(l.id)).length;
                if (badgeContainer) {
                  badgeContainer.innerHTML = `
                    ${activeInCatCount > 0 ? `<span class="gw-cat-active-pill">${activeInCatCount} configured</span>` : ''}
                    <span class="gw-cat-total-count">${catLayers.length} layers</span>
                  `;
                }
              }
            }
          };
        });
      });

      // Save Custom Activity
      const saveCustomBtn = this.container.querySelector('#gw-btn-save-custom-act') as HTMLButtonElement;
      if (saveCustomBtn) {
        saveCustomBtn.onclick = async () => {
          const trimmedTitle = this.customTitle.trim();
          if (!trimmedTitle) {
            alert('Please enter a name for your activity.');
            return;
          }
          if (this.tempPrimaryLayers.size === 0 && this.tempSecondaryLayers.size === 0) {
            alert('Please select at least one layer for your activity (Primary or Secondary).');
            return;
          }

          saveCustomBtn.disabled = true;
          saveCustomBtn.innerText = 'Saving...';

          const state = store.getState();
          let customList = [...(state.customActivities || [])];
          let enabledList = [...(state.enabledActivities || ['standard_forecast', 'hiking', 'photography', 'stargazing', 'winter_sports', 'water_sports', 'advanced'])];

          const actId = this.editingCustomActivityId || `custom_${Date.now()}`;
          const newCustomConfig: CustomActivityConfig = {
            id: actId,
            title: trimmedTitle,
            icon: this.customIcon || '📍',
            defaultBasemap: (this.customBasemap === 'terrain-mapterhorn' ? 'road' : this.customBasemap) || 'road',
            primaryLayerIds: Array.from(this.tempPrimaryLayers),
            secondaryLayerIds: Array.from(this.tempSecondaryLayers),
            updatedAt: Date.now()
          };

          if (this.editingCustomActivityId) {
            const idx = customList.findIndex(c => c.id === this.editingCustomActivityId);
            if (idx >= 0) customList[idx] = newCustomConfig;
            else customList.push(newCustomConfig);
          } else {
            customList.push(newCustomConfig);
            if (!enabledList.includes(actId)) {
              enabledList.push(actId);
            }
          }

          try {
            localStorage.setItem('gw_custom_activities', JSON.stringify(customList));
            localStorage.setItem('gw_enabled_activities', JSON.stringify(enabledList));
          } catch (_) { }

          store.update({
            customActivities: customList,
            enabledActivities: enabledList,
            activePersona: actId
          });

          await this.syncActivitiesToServer();

          this.isCreatingCustomActivity = false;
          this.editingCustomActivityId = null;
          this.render();
        };
      }

      // Activity Row Drag-and-Drop Reordering
      const activityRows = this.container.querySelectorAll<HTMLElement>('.gw-activity-row');
      activityRows.forEach((row) => {
        row.ondragstart = (e: DragEvent) => {
          const id = row.getAttribute('data-id');
          this.draggedActivityId = id;
          if (e.dataTransfer) {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', id || '');
          }
          row.classList.add('gw-row-dragging');
        };

        row.ondragover = (e: DragEvent) => {
          e.preventDefault();
          if (e.dataTransfer) {
            e.dataTransfer.dropEffect = 'move';
          }
        };

        row.ondragenter = (e: DragEvent) => {
          e.preventDefault();
          const id = row.getAttribute('data-id');
          if (this.draggedActivityId && this.draggedActivityId !== id) {
            row.classList.add('gw-row-drag-over');
          }
        };

        row.ondragleave = (e: DragEvent) => {
          const related = e.relatedTarget as Node | null;
          if (!related || !row.contains(related)) {
            row.classList.remove('gw-row-drag-over');
          }
        };

        row.ondrop = async (e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          row.classList.remove('gw-row-drag-over');

          const targetId = row.getAttribute('data-id');
          const sourceId = this.draggedActivityId || e.dataTransfer?.getData('text/plain');
          if (!sourceId || !targetId || sourceId === targetId) return;

          const state = store.getState();
          const list = [...state.enabledActivities];
          const srcIdx = list.indexOf(sourceId);
          const tgtIdx = list.indexOf(targetId);

          if (srcIdx >= 0 && tgtIdx >= 0) {
            list.splice(srcIdx, 1);
            list.splice(tgtIdx, 0, sourceId);
            try {
              localStorage.setItem('gw_enabled_activities', JSON.stringify(list));
            } catch (_) { }
            store.update({ enabledActivities: list });
            this.render();
            await this.syncActivitiesToServer();
          }
        };

        row.ondragend = () => {
          this.draggedActivityId = null;
          activityRows.forEach((r) => {
            r.classList.remove('gw-row-dragging');
            r.classList.remove('gw-row-drag-over');
          });
        };
      });

      // Reorder Up
      this.container.querySelectorAll('.gw-btn-up').forEach((btn) => {
        (btn as HTMLElement).onclick = async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (!id) return;
          const state = store.getState();
          const list = [...state.enabledActivities];
          const idx = list.indexOf(id);
          if (idx > 0) {
            const temp = list[idx - 1];
            list[idx - 1] = list[idx];
            list[idx] = temp;
            try { localStorage.setItem('gw_enabled_activities', JSON.stringify(list)); } catch (_) { }
            store.update({ enabledActivities: list });
            this.render();
            await this.syncActivitiesToServer();
          }
        };
      });

      // Reorder Down
      this.container.querySelectorAll('.gw-btn-down').forEach((btn) => {
        (btn as HTMLElement).onclick = async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (!id) return;
          const state = store.getState();
          const list = [...state.enabledActivities];
          const idx = list.indexOf(id);
          if (idx >= 0 && idx < list.length - 1) {
            const temp = list[idx + 1];
            list[idx + 1] = list[idx];
            list[idx] = temp;
            try { localStorage.setItem('gw_enabled_activities', JSON.stringify(list)); } catch (_) { }
            store.update({ enabledActivities: list });
            this.render();
            await this.syncActivitiesToServer();
          }
        };
      });

      // Remove from drawer
      this.container.querySelectorAll('.gw-btn-remove-act').forEach((btn) => {
        (btn as HTMLElement).onclick = async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (!id) return;
          const state = store.getState();
          if (state.enabledActivities.length <= 1) return;
          const nextEnabled = state.enabledActivities.filter(a => a !== id);
          let nextActive = state.activePersona;
          if (nextActive === id) {
            nextActive = nextEnabled[0] || 'hiking';
          }
          try {
            localStorage.setItem('gw_enabled_activities', JSON.stringify(nextEnabled));
            localStorage.setItem('gw_active_persona', nextActive);
          } catch (_) { }
          store.update({ enabledActivities: nextEnabled, activePersona: nextActive });
          this.render();
          await this.syncActivitiesToServer();
        };
      });

      // Add Canned to drawer
      this.container.querySelectorAll('.gw-btn-add-canned').forEach((btn) => {
        (btn as HTMLElement).onclick = async (e) => {
          e.preventDefault();
          e.stopPropagation();
          const id = btn.getAttribute('data-id');
          if (!id) return;
          const state = store.getState();
          if (!state.enabledActivities.includes(id)) {
            const nextEnabled = [...state.enabledActivities, id];
            try {
              localStorage.setItem('gw_enabled_activities', JSON.stringify(nextEnabled));
            } catch (_) { }
            store.update({ enabledActivities: nextEnabled });
            this.render();
            await this.syncActivitiesToServer();
          }
        };
      });

      // Edit Custom Activity
      this.container.querySelectorAll('.gw-btn-edit-act').forEach((btn) => {
        (btn as HTMLElement).onclick = () => {
          const id = btn.getAttribute('data-id');
          if (!id) return;
          const state = store.getState();
          const target = state.customActivities?.find(c => c.id === id);
          if (target) {
            this.isCreatingCustomActivity = true;
            this.editingCustomActivityId = target.id;
            this.customTitle = target.title;
            this.customIcon = target.icon;
            this.customBasemap = (target.defaultBasemap === 'terrain-mapterhorn' ? 'road' : target.defaultBasemap) || 'road';
            this.tempPrimaryLayers = new Set(target.primaryLayerIds || []);
            this.tempSecondaryLayers = new Set(target.secondaryLayerIds || []);
            this.layerSearchQuery = '';
            this.render();
          }
        };
      });

      // Delete Custom Activity
      this.container.querySelectorAll('.gw-btn-del-act').forEach((btn) => {
        (btn as HTMLElement).onclick = async () => {
          const id = btn.getAttribute('data-id');
          if (!id) return;
          if (!confirm('Are you sure you want to delete this custom activity?')) return;
          const state = store.getState();
          const nextCustom = (state.customActivities || []).filter(c => c.id !== id);
          const nextEnabled = (state.enabledActivities || []).filter(a => a !== id);
          let nextActive = state.activePersona;
          if (nextActive === id) {
            nextActive = nextEnabled[0] || 'hiking';
          }
          try {
            localStorage.setItem('gw_custom_activities', JSON.stringify(nextCustom));
            localStorage.setItem('gw_enabled_activities', JSON.stringify(nextEnabled));
            localStorage.setItem('gw_active_persona', nextActive);
          } catch (_) { }
          store.update({ customActivities: nextCustom, enabledActivities: nextEnabled, activePersona: nextActive });
          this.render();
          await this.syncActivitiesToServer();
        };
      });
    }

    // Add location placeholder button
    const addLocBtn = this.container.querySelector('#gw-profile-add-location-btn') as HTMLElement;
    if (addLocBtn) {
      addLocBtn.onclick = () => {
        const center = store.getState().mapCenter;
        alert(`Location pin saved for coordinates [${center[1].toFixed(4)}, ${center[0].toFixed(4)}] (Placeholder)`);
      };
    }

    // Feedback / Bug Report Form Handlers
    const feedbackTypeBtns = this.container.querySelectorAll('.gw-feedback-type-btn');
    feedbackTypeBtns.forEach(btn => {
      (btn as HTMLElement).onclick = () => {
        if (this.feedbackSubmitting) return;
        const cat = btn.getAttribute('data-cat') as 'bug' | 'feature' | 'feedback';
        if (cat) {
          this.feedbackCategory = cat;
          this.feedbackStatus = null;
          this.render();
        }
      };
    });

    const subjectInput = this.container.querySelector('#gw-feedback-subject') as HTMLInputElement;
    if (subjectInput) {
      subjectInput.oninput = () => {
        this.feedbackSubject = subjectInput.value;
      };
    }

    const messageTextarea = this.container.querySelector('#gw-feedback-message') as HTMLTextAreaElement;
    const charCountEl = this.container.querySelector('#gw-feedback-char-count') as HTMLElement;
    if (messageTextarea) {
      messageTextarea.oninput = () => {
        this.feedbackMessage = messageTextarea.value;
        if (charCountEl) {
          charCountEl.textContent = `${this.feedbackMessage.length} / 2000`;
        }
      };
    }

    const diagToggle = this.container.querySelector('#gw-feedback-diag-toggle') as HTMLInputElement;
    if (diagToggle) {
      diagToggle.onchange = () => {
        this.feedbackIncludeDiagnostics = diagToggle.checked;
      };
    }

    const submitFeedbackBtn = this.container.querySelector('#gw-feedback-submit-btn') as HTMLButtonElement;
    if (submitFeedbackBtn) {
      submitFeedbackBtn.onclick = () => {
        this.submitFeedback();
      };
    }
  }

  private async submitFeedback(): Promise<void> {
    if (this.feedbackSubmitting) return;

    const subject = this.feedbackSubject.trim();
    const message = this.feedbackMessage.trim();

    if (subject.length < 3) {
      this.feedbackStatus = {
        type: 'error',
        message: 'Please enter a brief subject (at least 3 characters).'
      };
      this.render();
      return;
    }

    if (message.length < 10) {
      this.feedbackStatus = {
        type: 'error',
        message: 'Please provide a few more details in your message (at least 10 characters).'
      };
      this.render();
      return;
    }

    this.feedbackSubmitting = true;
    this.feedbackStatus = null;
    this.render();

    try {
      const state = store.getState();
      const fbUser = (authService as any).auth?.currentUser;
      const idToken = fbUser ? await fbUser.getIdToken(false).catch(() => null) : null;
      const bearer = idToken || state.sessionToken || '';

      const hpEl = this.container?.querySelector('#gw-feedback-hp') as HTMLInputElement | null;
      const honeypot = hpEl?.value || '';

      let diagnostics: Record<string, any> | undefined;
      if (this.feedbackIncludeDiagnostics) {
        const center = state.mapCenter;
        diagnostics = {
          activePersona: state.activePersona,
          primaryProduct: state.primaryProduct || 'none',
          selectedModel: (state as any).selectedModel || 'default',
          mapCenter: Array.isArray(center) && center.length === 2
            ? `${Number(center[1]).toFixed(4)}, ${Number(center[0]).toFixed(4)}`
            : 'unknown',
          mapZoom: typeof (state as any).mapZoom === 'number' ? Number((state as any).mapZoom).toFixed(2) : undefined,
          cloudsEnabled: !!state.cloudsEnabled,
          windsEnabled: !!state.windsEnabled,
          smokeEnabled: !!state.smokeEnabled,
          wxStationsEnabled: !!state.wxStationsEnabled,
          theme: themeService.getTheme() || 'default',
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          userAgent: navigator.userAgent
        };
      }

      const apiBase = authService.getApiBase();
      const resp = await fetch(`${apiBase}/api/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(bearer ? { 'Authorization': `Bearer ${bearer}` } : {})
        },
        credentials: 'include',
        body: JSON.stringify({
          category: this.feedbackCategory,
          subject,
          message,
          platform: 'Web UI',
          userEmail: state.user?.email || '',
          userName: state.user?.displayName || '',
          website_url: honeypot,
          diagnostics
        })
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.error) {
        throw new Error(data.error || `Request failed (${resp.status})`);
      }

      this.feedbackSubject = '';
      this.feedbackMessage = '';
      this.feedbackStatus = {
        type: 'success',
        message: data.message || 'Thank you! Your report has been sent to the SkySkout team.'
      };
    } catch (err: any) {
      this.feedbackStatus = {
        type: 'error',
        message: err?.message || 'Unable to send report right now. Please try again shortly.'
      };
    } finally {
      this.feedbackSubmitting = false;
      this.render();
    }
  }

  private escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

export default AccountPanel;
